import { Injectable } from '@angular/core';
import { Http, RequestOptions, Headers } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { ApiService } from '../api.service';
import { IService } from '../interfaces/iservice';
import { ApiQueryParams } from '../api/api-query-params';
import { Selection, SelectionNew, SelectionUc, SelectionCancellation, ActionTypePatch, ActionTypeResultPatch, SoTypePatch } from 'ri-api-client';
import * as objectPath from 'object-path';
import * as popsicle from 'popsicle';

@Injectable()
export class SelectionService implements IService {
  public selections: Selection.Selection[] = [];
  public actionTypes: any[] = [];
  public selectionsUCs: SelectionUc.SelectionUc[] = [];
  public soccsTypes: any[] = [];
  public soTypes: any[] = [];
  public soTypesResults: any[] = [];
  public ccsgroupTypes: any[] = [];
  private options: RequestOptions;
  private goggleClassifications: any;

  constructor(private apiService: ApiService) {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    this.options = new RequestOptions({ headers: headers });
    this.apiService.init();
  }

  getSelection(id: number): Observable<Selection.Selection> {
    return this.apiService.client.selections
      .selectionId(id.toString())
      .GET()
      .map(res => {
        return this.apiService.handleResponse(res);
      });
  }

  post(selection: SelectionNew.SelectionNew): Observable<Selection.Selection> {
    return this.apiService.client.selections
      .POST(selection, this.options)
      .map(res => {
        return this.apiService.handleResponse(res);
      });
  }

  update(id: number, selection: SelectionNew.SelectionNew): Observable<Selection.Selection> {
    return this.apiService.client.selections
      .selectionId(id.toString())
      .PATCH(selection, this.options)
      .map(res => {
        return this.apiService.handleResponse(res);
      });
  }

  delete(selectionId: number) {
    return this.apiService.client.selections
      .selectionId(selectionId.toString())
      .DELETE(this.options)
      .map(res => {
        return this.apiService.handleResponse(res);
      });
  }

  clearUcsInSelection(selectionId: number): Observable<Selection.Selection> {
    return this.apiService.client.selections
      .selectionId(selectionId.toString())
      .ucs.DELETE(this.options)
      .map(res => {
        return this.apiService.handleResponse(res);
      });
  }

  addUcsToSelection(selectionId: number, ucIds: string[], patternIds: number[]) {
    const payload: SelectionUc.SelectionUc = {
      remove: false,
      ucsIds: ucIds,
      patternIds: patternIds
    };
    return this.apiService.client.selections
      .selectionId(selectionId.toString())
      .ucs.POST(payload)
      .map(res => {
        return this.apiService.handleResponse(res);
      });
  }

  removeUcsFromSelection(selectionId: number, ucIds: string[]) {
    const payload: SelectionUc.SelectionUc = {
      remove: true,
      ucsIds: ucIds
    };
    return this.apiService.client.selections
      .selectionId(selectionId.toString())
      .ucs.POST(payload)
      .map(res => {
        return this.apiService.handleResponse(res);
      });
  }

  finalizeSelections(selectionIds: number[]) {
    return this.apiService.client.selections.finalize.POST({
      selectionIds: selectionIds
    });
  }

  finalizeSelectionsWithSchedule(selectionIds: number[], selectionSchedule: any) {
    return this.apiService.client.selections.finalize.POST({
      selectionIds: selectionIds,
      originalSelectionId: selectionIds,
      sortProperties: selectionSchedule.sortProperties,
      patternsIds: selectionSchedule.patternsIds,
      patternsFilter: selectionSchedule.patternsFilter,
      targetsFilter: selectionSchedule.targetsFilter,
      startDate: selectionSchedule.startDate,
      endDate: selectionSchedule.endDate,
      recurrencyType: selectionSchedule.recurrencyType,
      recurrencyWeekDays: selectionSchedule.recurrencyWeekDays,
      scheduleEnabled: selectionSchedule.scheduleEnabled,
      context: selectionSchedule.context
    });
  }

  getAll(query: ApiQueryParams, opts?: string): Observable<any[]> {
    if (opts === 'pending') {
      return this.getPendingUCs(query);
    } else if (opts === 'cancelRequests') {
      return this.getCancelRequests(query);
    } else {
      return this.apiService.client.selections.GET(query.fields, query.filter, query.group, query.aggregatedFields, query.sort, query.page, query.skip, query.take)
        .map((res) => {
          return (this.selections = this.apiService.handleResponse(res));
        });
    }
  }

  getCount(filter: string, opts?: string): Observable<any> {
    if (opts === 'pending') {
      return this.getPendingUCs({
        filter: filter,
        aggregatedFields: 'Count()'
      });
    } else if (opts === 'cancelRequests') {
      return this.getCancelRequests({
        filter: filter,
        aggregatedFields: 'Count()'
      });
    } else {
      return this.getAll({
        filter: filter,
        aggregatedFields: 'Count()'
      });
    }
  }

  getTypes(): Observable<any> {
    return this.apiService.client.selections.actionTypes.GET()
      .map((res) => {
        return (this.actionTypes = this.apiService.handleResponse(res));
      });
  }

  updateTypesById(actionTypeId: string, body: ActionTypePatch.ActionTypePatch): Observable<any> {
    return this.apiService.client.selections.actionTypes.idActionType(actionTypeId).PATCH(body)
      .map((res) => {
        return (this.actionTypes = this.apiService.handleResponse(res));
      });
  }

  getSOTypesByActionTypesId(actionTypeId: string): Observable<any> {
    return this.apiService.client.selections.actionTypes.idActionType(actionTypeId).soTypes
      .GET().map((res) => (this.soTypes = this.apiService.handleResponse(res)));
  }

  getTypeResultsById(actionTypeId: string): Observable<any> {
    return this.apiService.client.selections.actionTypes.idActionType(actionTypeId).actionTypeResults.GET()
      .map((res) => {
        return (this.actionTypes = this.apiService.handleResponse(res));
      });
  }

  getTypeResults(onlyWithConfiguredMarkers = false): Observable<any> {
    return this.apiService.client.selections.actionTypeResults.GET(onlyWithConfiguredMarkers)
      .map((res) => {
        return (this.actionTypes = this.apiService.handleResponse(res));
      });
  }

  updateTypeResultsById(actionTypeResultsId: string, body: ActionTypeResultPatch.ActionTypeResultPatch): Observable<any> {
    return this.apiService.client.selections.actionTypeResults.idActionTypeResult(actionTypeResultsId).PATCH(body)
      .map((res) => {
        return (this.actionTypes = this.apiService.handleResponse(res));
      });
  }

  getSOTypes(onlyWithConfiguredMarkers = false, filter?: string): Observable<any> {
    return this.apiService.client.serviceOrders.soTypes.GET(onlyWithConfiguredMarkers, filter)
      .map((res) => this.soTypes = this.apiService.handleResponse(res));
  }

  updateSOTypesById(idSoType: string, body: SoTypePatch.SoTypePatch): Observable<any> {
    return this.apiService.client.serviceOrders.soTypes.PATCH(encodeURIComponent(idSoType), body)
      .map((res) => this.soTypes = this.apiService.handleResponse(res))
  }

  getSOTypesAndGroups(idSoType: string): Observable<any> {
    return this.apiService.client.serviceOrders.soTypes.soTypesAndGroups.GET(encodeURIComponent(idSoType))
      .map((res) => this.ccsgroupTypes = this.apiService.handleResponse(res));
  }

  getSOTypeResults(filter?: string): Observable<any> {
    return this.apiService.client.serviceOrders.soTypes.soTypeResults.GET(filter)
      .map((res) => this.soTypesResults = this.apiService.handleResponse(res));
  }

  getUcsInSelection(selectionId: number, fields?: string, filter?: string, start?: number, step?: number, patternsFilter?: string, selectionsFilter?: string): Observable<any[]> {
    let finalFilter = `Selections.Any(IdSelection eq ${selectionId})`;
    if (filter) {
      finalFilter += ` AND ${filter}`;
    }
    let page = null;
    if (start && step) {
      page = `${start},${step}`;
    }
    return this.apiService.client.ucs.search.GET(patternsFilter, selectionsFilter, fields, finalFilter, null, null, page, null, null, 'id', this.options)
      .map(res => {
        return this.apiService.handleResponse(res).results;
      });
  }

  getBlockedUcIdsInSelection(selectionId: number, filter: string): Observable<string[]> {
    return this.getUcsInSelection(
      selectionId,
      'Id',
      filter
    ).map(x => x.map(y => objectPath.get(y, 'id')));
  }

  cancelPendingSelections(selectionCancellation: SelectionCancellation.SelectionCancellation) {
    return this.apiService.client.selections.cancelPendingInspection
      .POST(selectionCancellation, this.options)
      .map(res => {
        return res.responseCode;
      });
  }

  exportUcsInSelections(selectionIds: number[], fields?: string, configSection?: string): Observable<any> {
    const selectionsFilter = selectionIds
      .map(id => `IdSelection eq ${id}`)
      .join(' or ');
    const filter = `Selections.Any(${selectionsFilter})`;
    const transport = popsicle.createTransport({ type: 'blob' });
    const options = {
      user: {
        sign: (x) => {
          x.transport = transport;
          return x;
        }
      }
    };
    return this.apiService.client.ucs.export.GET(null, selectionsFilter, configSection, fields, filter, null, null, 'ConsumerId', options);
  }

  getPendingUCs(query: ApiQueryParams): Observable<any> {
    return this.apiService.client.selections.pendingUcs.GET(query.fields, query.filter, query.group, query.aggregatedFields, query.sort, query.page, query.skip, query.take, query.opts)
      .map((res) => {
        return (this.selectionsUCs = this.apiService.handleResponse(res));
      });
  }

  getCancelRequests(query: ApiQueryParams): Observable<any> {
    return this.apiService.client.selections.cancelRequests.GET(query.fields, query.filter, query.group, query.aggregatedFields, query.sort, query.page, query.skip, query.take, query.opts)
      .map((res) => {
        return (this.selectionsUCs = this.apiService.handleResponse(res));
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/core/api/selection.service.ts